import { useState, type ReactNode } from 'react';

import { cn } from '../ui/cn';

import type { Brand } from './resolve-brand';

/**
 * Logo do corban nos dois temas, trocada só por CSS (`data-theme`), sem re-render. Sem logo escura,
 * ou se ela não carregar (o `INVERTED_LOGO` da UY3 hoje responde 403), o escuro usa a clara, ou o
 * `darkFallback` quando houver. Se a clara também falhar, vale o `fallback`. Nunca `filter: invert()`.
 * `surface="stage"` é o fundo escuro fixo do palco (login): usa a logo escura e, sem ela, a clara num
 * chip claro.
 */
export function BrandLogo({
  brand,
  variant,
  className,
  fallback,
  darkFallback,
  surface = 'theme',
}: {
  brand: Brand;
  variant: 'full' | 'mark';
  className?: string;
  fallback: ReactNode;
  darkFallback?: ReactNode;
  surface?: 'theme' | 'stage';
}) {
  const src = variant === 'full' ? brand.logo : brand.mark;
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());
  const fail = (url: string) => () => setBroken((prev) => new Set(prev).add(url));
  const light = src.light && !broken.has(src.light) ? src.light : undefined;
  const dark = src.dark && src.dark !== light && !broken.has(src.dark) ? src.dark : undefined;
  const img = 'block h-full w-auto max-w-full object-contain';
  if (surface === 'stage') {
    if (dark)
      return (
        <span className={cn('inline-flex items-center', className)}>
          <img src={dark} alt={brand.name} decoding="async" onError={fail(dark)} className={img} />
        </span>
      );
    if (!light) return <>{fallback}</>;
    return (
      <span className={cn('inline-flex items-center rounded-lg bg-stage-foreground px-2.5 py-1.5', className)}>
        <img src={light} alt={brand.name} decoding="async" onError={fail(light)} className={img} />
      </span>
    );
  }
  if (!light) return <>{fallback}</>;
  const swap = !dark && darkFallback !== undefined;
  return (
    <>
      <span className={cn('inline-flex items-center', className, swap && 'in-data-[theme=dark]:hidden')}>
        <img src={light} alt={brand.name} decoding="async" onError={fail(light)} className={cn(img, dark && 'in-data-[theme=dark]:hidden')} />
        {dark ? <img src={dark} alt={brand.name} decoding="async" onError={fail(dark)} className={cn(img, 'hidden in-data-[theme=dark]:block')} /> : null}
      </span>
      {swap ? <span className="hidden in-data-[theme=dark]:contents">{darkFallback}</span> : null}
    </>
  );
}
